import { useDraggable, useDroppable } from '@dnd-kit/core'
import { DateTime, Interval } from 'luxon'
import { Fragment } from 'react'
import { Location, Vehicle } from '../../shared/types'
import { DashboardEntry } from '../api/dashboard'
import { ROW_HEIGHT } from './consts'
import { NewDragOverlay } from './drag-overlays'
import { Entry, RecurringEntry } from './entry'

export function DashboardRow({
	vehicle,
	locations,
	list,
	interval,
	seconds,
}: {
	vehicle: Vehicle
	locations: Record<string, Location>
	list: DashboardEntry[]
	interval: Interval<true>
	seconds: number
}) {
	const { setNodeRef, rect, isOver, active } = useDroppable({
		id: vehicle.id,
	})
	const draggable = useDraggable({
		id: vehicle.id,
		data: {
			type: 'new',
		},
	})
	const stack: Array<DateTime> = []
	let top = 0
	let max = 0
	const hightlight = isOver && active?.data.current?.type === 'center'

	return (
		<Fragment key={vehicle.id}>
			<div
				className={`border-b border-slate-300 flex items-center gap-2 space-between px-4 text-xs text-left font-light ${hightlight ? 'bg-rose-100' : 'bg-gray-50'}`}
			>
				<div className="flex-1 overflow-hidden text-nowrap">{vehicle.name}</div>
				<code className="flex-0 text-right text-xs">{vehicle.license_plate}</code>
			</div>
			<div
				className={`border-b border-slate-300 hover:border-slate-400 ${hightlight ? 'bg-rose-100' : ''}`}
				ref={setNodeRef}
			>
				<div className="relative">
					<div
						className="absolute inset-0"
						ref={draggable.setNodeRef}
						{...draggable.listeners}
						{...draggable.attributes}
					>
						{draggable.isDragging ? (
							<NewDragOverlay
								left={draggable.transform?.x as number}
								width={draggable.transform?.y as number}
								seconds={seconds}
								interval={interval}
								containerRect={rect}
							/>
						) : null}
					</div>
					{list.map(entry => {
						const dateFrom = DateTime.max(interval.start, entry.dateFrom)
						const dateTo = DateTime.min(interval.end, entry.dateTo)

						top = getNextRow(stack, top, dateFrom, dateTo, entry.recurring, interval)

						if (top >= max) max = top

						if (dateTo < interval.start) {
							return (
								<RecurringEntry
									key={entry.id}
									entry={entry}
									top={top * ROW_HEIGHT}
									interval={interval}
									seconds={seconds}
									dateFrom={dateFrom}
									locations={locations}
								/>
							)
						}

						return (
							<Fragment key={entry.id}>
								<Entry
									entry={entry}
									top={top * ROW_HEIGHT}
									interval={interval}
									seconds={seconds}
									dateFrom={dateFrom}
									dateTo={dateTo}
									containerRect={rect}
									locations={locations}
								/>
								{entry.recurring && dateTo < interval.end ? (
									<RecurringEntry
										entry={entry}
										top={top * ROW_HEIGHT}
										interval={interval}
										seconds={seconds}
										dateFrom={dateTo}
										locations={locations}
									/>
								) : null}
							</Fragment>
						)
					})}
					<div className="w-0" style={{ height: (max + 1) * ROW_HEIGHT }}></div>
				</div>
			</div>
		</Fragment>
	)
}

function getNextRow(
	stack: Array<DateTime>,
	top: number,
	dateFrom: DateTime,
	dateTo: DateTime,
	recurring: boolean,
	interval: Interval<true>,
) {
	if (recurring) {
		stack.push(interval.end)
		return top
	}

	if (stack.length) {
		top++
		if (stack.at(-1)! >= dateFrom) {
			stack.push(dateTo)
		} else {
			while (stack.length && stack.at(-1)! < dateFrom) {
				stack.pop()
				top--
			}
		}
	}
	if (!stack.length) {
		stack.push(dateTo)
	}

	return top
}
